/**
 * Turning "these installments, quoted by the server" into a gateway payment.
 *
 * The v2 gateway payment takes a list of items and settles each one on its own
 * after the bank verifies, so N installments are one trip to the bank and N
 * repayment cash-ins — no backend change was needed to pay several at once,
 * only a client that stops sending lists of length one.
 */

export interface QuotedInstallment {
    id: number
    amount: number
    index?: number
}

/**
 * The list this screen was navigated with.
 *
 * Accepts the single-installment shape the loan page used to send
 * (`selectedInstallment` + the amount on `item`) so an in-flight navigation,
 * a restored history entry or a deep link from an older bundle still pays.
 */
export const installmentsFromState = (state: any): QuotedInstallment[] => {
    if (Array.isArray(state?.installments) && state.installments.length > 0) {
        return state.installments
    }
    if (state?.selectedInstallment) {
        return [
            {
                id: Number(state.selectedInstallment),
                amount: Number(state?.item?.unpaid_portion ?? 0),
            },
        ]
    }
    return []
}

/** The poolship the repayment is credited to, across both shapes. */
export const poolshipFromState = (state: any): number | undefined => {
    const id = state?.poolshipId ?? state?.item?.id
    return id ? Number(id) : undefined
}

export const totalOf = (installments: QuotedInstallment[]) =>
    installments.reduce((sum, one) => sum + (one.amount || 0), 0)

export const commissionOf = (receivers?: Array<{ commission: number }>) =>
    (receivers ?? []).reduce((sum, one) => sum + (one.commission || 0), 0)

/**
 * One payment item per installment, each carrying the commission the server
 * quoted for it.
 *
 * Returns null when the quote does not line up one-for-one with the basket.
 * Commissions are matched by POSITION — the quote echoes the poolship id,
 * which is the same on every row — so a short or reordered `receivers` array
 * would silently attach the wrong fee (or none) to an installment. Charging a
 * fee the member never agreed to, or billing the fund nothing, is worse than
 * asking them to try again.
 */
export const buildPaymentItems = (
    installments: QuotedInstallment[],
    receivers: Array<{ commission: number }> | undefined,
    boxId: number
): IPaymentItems[] | null => {
    if (installments.length === 0) return null
    if (!receivers || receivers.length !== installments.length) return null

    return installments.map((installment, position) => ({
        itemAmount: Number(installment.amount),
        itemType: 'ins',
        itemId: installment.id,
        box_id: boxId,
        commission: Number(receivers[position].commission || 0),
        description: installment.index
            ? `پرداخت قسط ${installment.index}`
            : 'پرداخت قسط',
    }))
}
